/**
 * ConditionRulesPanel — zakładka reguł UPRAWNIEŃ WARUNKOWYCH w Centrum (F24).
 *
 * Reguła jest tu bytem NAZWANYM: buduje się ją raz („Faktury do 5 tys. z mojego działu") i
 * przypina wielu rolom, zespołom i osobom. To dlatego nie mieszka w siatce uprawnień — tam
 * wszystko kręci się wokół jednego wybranego podmiotu, a warunek z założenia dotyczy wielu.
 *
 * **Reguła wyłącznie DODAJE prawo.** Działa tam, gdzie zwykła ocena nic nie rozstrzygnęła, więc
 * żeby zadziałała, komórka w siatce musi zostać na „zgodnie z rolą". Jawny zakaz ją wyłącza —
 * i to jest jedyne miejsce, w którym ekran zachowuje się inaczej, niż podpowiada intuicja,
 * dlatego mówimy o tym wprost na górze listy.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PermissionAction, PermissionCondition } from '@saas/shared';
import { AlertTriangle, Pencil, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { IconButton } from '@/components/IconButton';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { Select } from '@/components/Select';
import { useToast } from '@/contexts/ToastContext';
import {
  type ConditionRule,
  type ConditionRuleInput,
  type ConditionRuleSubject,
  useConditionFields,
  useConditionRules,
  useDeleteConditionRule,
  useSaveConditionRule,
} from '@/lib/use-condition-rules';

import {
  ConditionBuilder,
  conditionMissesValue,
  conditionTooDeep,
  emptyCondition,
} from './ConditionBuilder';
import {
  PERMISSION_TABLE_CARD_CLASS,
  PERMISSION_TABLE_CLASS,
  PERMISSION_THEAD_CLASS,
} from './permission-table-classes';

const ACTIONS: PermissionAction[] = ['view', 'edit', 'delete'];

export interface ConditionRulesPanelProps {
  companyId: string | null;
  /** Kartoteki do wyboru — te same, które widać w drzewie uprawnień. */
  kartoteki: Array<{ key: string; label: string }>;
  /** Podmioty, którym można przypiąć regułę. */
  subjects: Array<{ kind: 'role' | 'team' | 'user'; id: string; name: string }>;
  /**
   * Klasy dokumentów do zawężenia reguły — z drzewa uprawnień.
   *
   * Zawężenie do klasy nie jest kosmetyką: pola dodatkowe i tabele dodatkowe dokumentów żyją
   * OSOBNO dla każdej klasy, więc bez wskazania klasy nie ma jak ich zaproponować.
   */
  docClasses: Array<{ key: string; label: string }>;
  /** Podmiot wybrany na ekranie obok — zaznaczamy go od razu w nowej regule. */
  preselectedSubject: ConditionRuleSubject | null;
  /**
   * Czy użytkownik może zarządzać uprawnieniami.
   *
   * Świadomie NIE jest to tryb edycji siatki obok: tam zmiany zbierają się w szkicu i zapisują
   * jednym przyciskiem, a reguła warunkowa ma własne okno i zapisuje się sama. Wspólny przełącznik
   * znaczyłby, że po dodaniu reguły trzeba jeszcze nacisnąć „Zapisz", który nie dotyczy niczego.
   */
  canManage: boolean;
}

export function ConditionRulesPanel({
  companyId,
  kartoteki,
  docClasses,
  subjects,
  preselectedSubject,
  canManage,
}: ConditionRulesPanelProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const rules = useConditionRules(companyId);
  const save = useSaveConditionRule(companyId);
  const remove = useDeleteConditionRule(companyId);

  const [draft, setDraft] = useState<DraftState | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<ConditionRule | null>(null);

  const subjectName = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of subjects) map.set(`${s.kind}:${s.id}`, s.name);
    return map;
  }, [subjects]);

  const list = rules.data?.rules ?? [];

  return (
    <div className="flex flex-col gap-3 p-3">
      <p className="rounded-md bg-surface-secondary px-3 py-2 text-xs text-text-secondary">
        {t('tenant.permissions.conditions.intro')}
      </p>

      {canManage ? (
        <div>
          <Button
            size="sm"
            onClick={() => setDraft(newDraft(kartoteki[0]?.key ?? 'documents', preselectedSubject))}
            disabled={kartoteki.length === 0}
          >
            <Plus className="mr-1 h-3 w-3" />
            {t('tenant.permissions.conditions.add')}
          </Button>
        </div>
      ) : null}

      {list.length === 0 ? (
        <p className="px-1 py-6 text-center text-sm text-text-secondary">
          {t('tenant.permissions.conditions.empty')}
        </p>
      ) : (
        <div className={PERMISSION_TABLE_CARD_CLASS}>
          <table className={PERMISSION_TABLE_CLASS} data-skin="antd">
            <thead className={PERMISSION_THEAD_CLASS}>
              <tr className="text-left text-text-secondary">
                <th className="px-2 py-2 font-medium">{t('tenant.permissions.conditions.name')}</th>
                <th className="px-2 py-2 font-medium">
                  {t('tenant.permissions.conditions.scope')}
                </th>
                <th className="px-2 py-2 font-medium">
                  {t('tenant.permissions.conditions.actions')}
                </th>
                <th className="px-2 py-2 font-medium">
                  {t('tenant.permissions.conditions.appliesTo')}
                </th>
                <th className="w-20" />
              </tr>
            </thead>
            <tbody>
              {list.map((rule) => (
                <tr key={rule.id} className="border-b border-border-primary/60">
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-2">
                      {!rule.isEnabled || rule.brokenReason ? (
                        <AlertTriangle
                          className="h-3 w-3 text-status-warning"
                          aria-label={
                            rule.brokenReason
                              ? t('tenant.permissions.conditions.broken')
                              : t('tenant.permissions.conditions.disabled')
                          }
                        />
                      ) : null}
                      <span className={rule.isEnabled ? '' : 'text-text-secondary line-through'}>
                        {rule.name}
                      </span>
                    </div>
                    {rule.description ? (
                      <div className="text-xs text-text-secondary">{rule.description}</div>
                    ) : null}
                    {rule.brokenReason ? (
                      <div className="text-xs text-status-warning">{rule.brokenReason}</div>
                    ) : null}
                  </td>
                  <td className="px-2 py-2 text-text-secondary">
                    {kartoteki.find((k) => k.key === rule.kartotekaKey)?.label ?? rule.kartotekaKey}
                  </td>
                  <td className="px-2 py-2 text-text-secondary">
                    {rule.actions.map((a) => t(`tenant.permissions.action.${a}`)).join(', ')}
                  </td>
                  <td className="px-2 py-2 text-text-secondary">
                    {rule.subjects.length === 0
                      ? t('tenant.permissions.conditions.noSubjects')
                      : rule.subjects
                          .map((s) => subjectName.get(`${s.kind}:${s.id}`) ?? s.id)
                          .join(', ')}
                  </td>
                  <td className="px-2 py-2">
                    {canManage ? (
                      <div className="flex justify-end gap-1">
                        <IconButton
                          size="sm"
                          variant="ghost"
                          aria-label={t('common.edit')}
                          onClick={() => setDraft(draftFrom(rule))}
                        >
                          <Pencil className="h-3 w-3" />
                        </IconButton>
                        <IconButton
                          size="sm"
                          variant="ghost"
                          aria-label={t('common.delete')}
                          onClick={() => setConfirmDelete(rule)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </IconButton>
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {draft && companyId ? (
        <RuleEditorModal
          draft={draft}
          companyId={companyId}
          kartoteki={kartoteki}
          docClasses={docClasses}
          subjects={subjects}
          saving={save.isPending}
          onClose={() => setDraft(null)}
          onChange={setDraft}
          onSave={async () => {
            try {
              await save.mutateAsync({
                ruleId: draft.id,
                input: toInput(draft, companyId),
              });
              toast.show({ variant: 'success', title: t('tenant.permissions.conditions.saved') });
              setDraft(null);
            } catch {
              toast.show({
                variant: 'error',
                title: t('tenant.permissions.conditions.saveFailed'),
              });
            }
          }}
        />
      ) : null}

      <ConfirmDialog
        open={confirmDelete != null}
        title={t('tenant.permissions.conditions.deleteTitle')}
        message={t('tenant.permissions.conditions.deleteMessage', {
          name: confirmDelete?.name ?? '',
        })}
        variant="danger"
        confirmLabel={t('common.delete')}
        onConfirm={async () => {
          if (!confirmDelete) return;
          try {
            await remove.mutateAsync(confirmDelete.id);
            toast.show({ variant: 'success', title: t('tenant.permissions.conditions.deleted') });
          } catch {
            toast.show({
              variant: 'error',
              title: t('tenant.permissions.conditions.deleteFailed'),
            });
          }
          setConfirmDelete(null);
        }}
        onClose={() => setConfirmDelete(null)}
      />
    </div>
  );
}

// ─── Edytor ───────────────────────────────────────────────────────────────────

interface DraftState {
  id: string | null;
  name: string;
  description: string;
  kartotekaKey: string;
  /** Pusty napis = reguła dotyczy całej kartoteki. */
  docClass: string;
  actions: PermissionAction[];
  condition: PermissionCondition | null;
  isEnabled: boolean;
  subjects: ConditionRuleSubject[];
}

function newDraft(kartotekaKey: string, subject: ConditionRuleSubject | null): DraftState {
  return {
    id: null,
    name: '',
    description: '',
    kartotekaKey,
    docClass: '',
    actions: ['view'],
    // Warunek dobudujemy, gdy znane będą pola kartoteki — pusty wybór nie dałby się zapisać.
    condition: null,
    isEnabled: true,
    // Podmiot wybrany na ekranie obok jest niemal zawsze tym, dla którego reguła powstaje —
    // zaznaczamy go od razu, żeby najczęstszy przypadek nie wymagał żadnego kliknięcia.
    subjects: subject ? [subject] : [],
  };
}

function draftFrom(rule: ConditionRule): DraftState {
  return {
    id: rule.id,
    name: rule.name,
    description: rule.description ?? '',
    kartotekaKey: rule.kartotekaKey,
    docClass: rule.objectKind === 'doc_class' ? (rule.objectKey ?? '') : '',
    actions: rule.actions,
    condition: rule.condition,
    isEnabled: rule.isEnabled,
    subjects: rule.subjects,
  };
}

function toInput(draft: DraftState, companyId: string): ConditionRuleInput {
  return {
    companyId,
    name: draft.name.trim(),
    description: draft.description.trim() || null,
    kartotekaKey: draft.kartotekaKey,
    objectKind: draft.docClass ? 'doc_class' : 'kartoteka',
    objectKey: draft.docClass || null,
    objectId: null,
    actions: draft.actions,
    condition: draft.condition!,
    isEnabled: draft.isEnabled,
    subjects: draft.subjects,
  };
}

interface EditorProps {
  draft: DraftState;
  companyId: string;
  kartoteki: Array<{ key: string; label: string }>;
  docClasses: Array<{ key: string; label: string }>;
  subjects: Array<{ kind: 'role' | 'team' | 'user'; id: string; name: string }>;
  saving: boolean;
  onChange: (next: DraftState) => void;
  onSave: () => void;
  onClose: () => void;
}

function RuleEditorModal({
  draft,
  companyId,
  kartoteki,
  docClasses,
  subjects,
  saving,
  onChange,
  onSave,
  onClose,
}: EditorProps) {
  const { t } = useTranslation();
  const fields = useConditionFields(companyId, draft.kartotekaKey, draft.docClass || null);
  const groups = fields.data?.groups ?? [];

  // Warunek startowy powstaje dopiero, gdy wiadomo, jakie pola ma ta kartoteka.
  const condition = draft.condition ?? (groups.length > 0 ? emptyCondition(groups) : null);

  const nameOk = draft.name.trim().length > 0;
  const depthOk = condition ? !conditionTooDeep(condition) : false;
  // Warunek bez wartości serwer odrzuci — blokujemy zapis od razu i mówimy, co uzupełnić.
  const missesValue = condition ? conditionMissesValue(condition) : false;
  const canSave =
    nameOk && draft.actions.length > 0 && condition != null && depthOk && !missesValue;

  return (
    <Modal
      open
      onClose={onClose}
      title={
        draft.id
          ? t('tenant.permissions.conditions.editTitle')
          : t('tenant.permissions.conditions.addTitle')
      }
      size="xl"
    >
      <div className="flex flex-col gap-4">
        <Input
          label={t('tenant.permissions.conditions.name')}
          value={draft.name}
          onChange={(e) => onChange({ ...draft, name: e.target.value })}
          placeholder={t('tenant.permissions.conditions.namePlaceholder')}
        />

        <Input
          label={t('tenant.permissions.conditions.description')}
          value={draft.description}
          onChange={(e) => onChange({ ...draft, description: e.target.value })}
        />

        <Select
          label={t('tenant.permissions.conditions.kartoteka')}
          value={draft.kartotekaKey}
          onChange={(e) =>
            // Zmiana kartoteki unieważnia warunek — pola są inne, więc stary wskazywałby w próżnię.
            onChange({ ...draft, kartotekaKey: e.target.value, condition: null })
          }
          options={kartoteki.map((k) => ({ value: k.key, label: k.label }))}
        />

        {draft.kartotekaKey === 'documents' && docClasses.length > 0 ? (
          <Select
            label={t('tenant.permissions.conditions.docClass')}
            hint={t('tenant.permissions.conditions.docClassHint')}
            value={draft.docClass}
            onChange={(e) =>
              // Zmiana klasy zmienia zestaw pól dodatkowych, więc stary warunek mógłby wskazywać
              // w próżnię — budujemy go od nowa.
              onChange({ ...draft, docClass: e.target.value, condition: null })
            }
            options={[
              { value: '', label: t('tenant.permissions.conditions.allClasses') },
              ...docClasses.map((c) => ({ value: c.key, label: c.label })),
            ]}
          />
        ) : null}

        <fieldset>
          <legend className="mb-1 text-sm font-medium">
            {t('tenant.permissions.conditions.actions')}
          </legend>
          <div className="flex gap-4">
            {ACTIONS.map((action) => (
              <Checkbox
                key={action}
                label={t(`tenant.permissions.action.${action}`)}
                checked={draft.actions.includes(action)}
                onChange={(e) =>
                  onChange({
                    ...draft,
                    actions: e.target.checked
                      ? [...draft.actions, action]
                      : draft.actions.filter((a) => a !== action),
                  })
                }
              />
            ))}
          </div>
        </fieldset>

        <div>
          <div className="mb-1 text-sm font-medium">
            {t('tenant.permissions.conditions.condition')}
          </div>
          {fields.isLoading ? (
            <p className="text-sm text-text-secondary">{t('common.loading')}</p>
          ) : condition ? (
            <>
              <ConditionBuilder
                value={condition}
                groups={groups}
                onChange={(next) => onChange({ ...draft, condition: next })}
              />
              {missesValue ? (
                <p className="mt-1 text-xs text-text-secondary">
                  {t('tenant.permissions.conditions.valueRequiredHint')}
                </p>
              ) : null}
            </>
          ) : (
            <p className="text-sm text-text-secondary">
              {t('tenant.permissions.conditions.noFields')}
            </p>
          )}
        </div>

        <fieldset>
          <legend className="mb-1 text-sm font-medium">
            {t('tenant.permissions.conditions.appliesTo')}
          </legend>
          <div className="max-h-48 overflow-y-auto rounded-md border border-border-primary p-2">
            {subjects.map((s) => {
              const key = `${s.kind}:${s.id}`;
              const checked = draft.subjects.some((x) => x.kind === s.kind && x.id === s.id);
              return (
                <Checkbox
                  key={key}
                  label={`${t(`tenant.permissions.subject.${s.kind}`)}: ${s.name}`}
                  checked={checked}
                  onChange={(e) =>
                    onChange({
                      ...draft,
                      subjects: e.target.checked
                        ? [...draft.subjects, { kind: s.kind, id: s.id }]
                        : draft.subjects.filter((x) => !(x.kind === s.kind && x.id === s.id)),
                    })
                  }
                />
              );
            })}
          </div>
          {draft.subjects.length === 0 ? (
            <p className="mt-1 text-xs text-text-secondary">
              {t('tenant.permissions.conditions.noSubjectsHint')}
            </p>
          ) : null}
        </fieldset>

        <Checkbox
          label={t('tenant.permissions.conditions.enabled')}
          checked={draft.isEnabled}
          onChange={(e) => onChange({ ...draft, isEnabled: e.target.checked })}
        />

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => {
              if (condition && draft.condition !== condition) {
                onChange({ ...draft, condition });
              }
              onSave();
            }}
            disabled={!canSave || saving}
          >
            {t('common.save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
